import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Calendar, ArrowRight, FileText, Tag } from "lucide-react";
import { API } from "../../data/constants";
import { SEO } from "../../components/SEO";
import { formatPostDate, readingTimeFromHtml } from "./blogHelpers";

const PostCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="bg-white rounded-2xl shadow-card overflow-hidden card-hover block"
    data-testid={`blog-card-${post.slug}`}
  >
    {post.cover_image_url ? (
      <img src={post.cover_image_url} alt={post.title} className="w-full h-48 object-cover" />
    ) : (
      <div className="w-full h-48 bg-gradient-to-br from-orange-light to-orange-primary/20 flex items-center justify-center">
        <FileText className="text-orange-primary/40" size={56} />
      </div>
    )}
    <div className="p-6">
      <div className="flex items-center gap-2 text-xs text-gray-medium mb-2">
        {post.category && <span className="bg-orange-primary/10 text-orange-primary px-2 py-0.5 rounded-full font-medium">{post.category}</span>}
        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatPostDate(post.published_at)}</span>
        <span>· {readingTimeFromHtml(post.content)} min read</span>
      </div>
      <h3 className="font-display font-bold text-xl text-gray-dark mb-2 line-clamp-2">{post.title}</h3>
      {post.excerpt && <p className="text-gray-medium text-sm line-clamp-3">{post.excerpt}</p>}
      <span className="inline-flex items-center gap-1 text-orange-primary font-semibold text-sm mt-4">
        Read more <ArrowRight size={14} />
      </span>
    </div>
  </Link>
);

const BlogIndexPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const category = searchParams.get("category") || "";
  const tag = searchParams.get("tag") || "";
  const search = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (tag) params.tag = tag;
    if (search) params.search = search;
    axios.get(`${API}/blog/posts`, { params })
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category, tag, search]);

  useEffect(() => {
    axios.get(`${API}/blog/categories`).then(({ data }) => setCategories(data)).catch(() => {});
    axios.get(`${API}/blog/tags`).then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput) next.set("q", searchInput); else next.delete("q");
    setSearchParams(next);
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const activeBadges = [
    category && { key: "category", value: category, label: `Category: ${category}` },
    tag && { key: "tag", value: tag, label: `Tag: ${tag}` },
    search && { key: "q", value: search, label: `Search: "${search}"` }
  ].filter(Boolean);

  return (
    <div className="pt-20 min-h-screen bg-gray-light" data-testid="blog-index-page">
      <SEO
        title="Resources & Insights"
        description="Foot-traffic marketing, SmartReach™ playbooks, industry case studies, and local advertising insights from the FunAsia Digital team."
        path="/blog"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">Resources & Insights</h1>
          <p className="text-lg text-gray-medium max-w-2xl mx-auto">
            Foot-traffic playbooks, industry case studies, and SmartReach™ deep-dives — written for local business operators.
          </p>
          <form onSubmit={submitSearch} className="mt-8 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-medium" size={18} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search posts…"
              className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:ring-2 focus:ring-orange-primary outline-none bg-white shadow-card"
              data-testid="blog-search-input"
            />
          </form>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {(categories.length > 0 || tags.length > 0) && (
            <div className="bg-white rounded-2xl p-5 shadow-card mb-8">
              {categories.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-display font-bold uppercase text-gray-medium tracking-wider mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilter("category", "")}
                      className={`px-3 py-1 rounded-full text-sm ${!category ? "bg-orange-primary text-white" : "bg-gray-light text-gray-dark hover:bg-orange-primary/10"}`}
                    >All</button>
                    {categories.map((c) => (
                      <button
                        key={c}
                        onClick={() => setFilter("category", c)}
                        className={`px-3 py-1 rounded-full text-sm ${category === c ? "bg-orange-primary text-white" : "bg-gray-light text-gray-dark hover:bg-orange-primary/10"}`}
                      >{c}</button>
                    ))}
                  </div>
                </div>
              )}
              {tags.length > 0 && (
                <div>
                  <p className="text-xs font-display font-bold uppercase text-gray-medium tracking-wider mb-2 inline-flex items-center gap-1"><Tag size={11} /> Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <button
                        key={t}
                        onClick={() => setFilter("tag", tag === t ? "" : t)}
                        className={`px-3 py-1 rounded-full text-xs ${tag === t ? "bg-orange-primary text-white" : "bg-orange-light text-orange-primary hover:bg-orange-primary/20"}`}
                      >#{t}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeBadges.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {activeBadges.map((b) => (
                <button key={b.key} onClick={() => setFilter(b.key, "")} className="text-xs bg-white border border-gray-200 px-3 py-1 rounded-full hover:bg-gray-light">
                  {b.label} ✕
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p className="text-center text-gray-medium py-20">Loading…</p>
          ) : posts.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center text-gray-medium">
              <FileText className="mx-auto mb-3 text-orange-primary/40" size={48} />
              <p className="text-lg font-display text-gray-dark mb-1">No posts match your filters.</p>
              <p className="text-sm">Try clearing filters or check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogIndexPage;
